import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import JobCard from 'components/JobCard/JobCard';
import { mapBoardExternalJobToJobCardProps } from 'components/JobCard/mapBoardExternalJobToJobCardProps';
import JobsBoardBanner from 'containers/Ads/JobsBoardBanner/JobsBoardBanner';
import BrowseMoreJobs from 'components/BrowseMoreJobs/BrowseMoreJobs';
import JobsBestMatchCard from 'components/JobsBestMatchCard/JobsBestMatchCard';
import { trackEvent } from 'helpers/Analytics';
import {
  formatDayHeading,
  showAllExternalLabel,
} from 'containers/Projects/jobsTimeline';
import {
  EXTERNAL_JOBS_PER_DAY,
  EXTERNAL_JOBS_PER_PAGE,
} from 'redux/modules/externalJobs';
import styles from './JobsTimeline.module.scss';

/** Partner ads after the card at index 1, then every 15th card (14, 29, 44…). */
export const shouldInjectJobsBoardAd = (cardIndex) =>
  cardIndex === 1 || (cardIndex + 1) % 15 === 0;

/** Guest browse-more box after this Twine job (end of the first batch of 10). */
export const JOBS_BROWSE_MORE_INDEX = 9;

const MAX_BANNER_COUNT = 5;

const SectionHeading = ({ kind }) =>
  kind === 'twine' ? (
    <div className={styles.sectionHeading}>
      <span className={`${styles.sectionBadge} ${styles.sectionBadgeTwine}`}>
        Twine
      </span>
      <h3 className={styles.sectionTitle}>On Twine</h3>
      <span className={styles.sectionHint}>Apply directly</span>
      <span className={styles.sectionRule} aria-hidden="true" />
    </div>
  ) : (
    <div className={styles.sectionHeading}>
      <span className={`${styles.sectionBadge} ${styles.sectionBadgeExternal}`}>
        External
      </span>
      <h3 className={styles.sectionTitle}>From other job sites</h3>
      <span className={styles.sectionHint}>
        Apply on the company&apos;s site
      </span>
      <span className={styles.sectionRule} aria-hidden="true" />
    </div>
  );

SectionHeading.propTypes = {
  kind: PropTypes.oneOf(['twine', 'external']).isRequired,
};

/**
 * Newest first on /jobs: featured (boosted) jobs, then one section per day
 * with Twine jobs first and jobs from other sites after them.
 */
const JobsTimeline = ({
  timeline,
  today,
  expandedDays,
  onShowAllExternal,
  onClickRole,
  onClickLocation,
  partners,
  loadedPartners,
  loadingPartners,
  pageType,
  auth,
  filterCount,
  onBestMatchNavigate,
  showBestMatchCard,
  country,
}) => {
  let cardIndex = 0;
  let twineIndex = 0;
  let bannerCount = 0;

  // Cards share one running index so ads keep their spacing across days.
  const withInserts = (card, key, isTwine) => {
    const index = cardIndex;
    cardIndex += 1;
    const injectAd =
      shouldInjectJobsBoardAd(index) && bannerCount < MAX_BANNER_COUNT;
    if (injectAd) {
      bannerCount += 1;
    }

    let twineInserts = null;
    if (isTwine) {
      const position = twineIndex;
      twineIndex += 1;
      twineInserts = auth === 'guest' &&
        position === JOBS_BROWSE_MORE_INDEX && (
          <>
            <div
              className={styles.batchDivider}
              data-testid="jobs-batch-divider"
            >
              <hr className={styles.batchDividerRule} />
              <span className={styles.batchDividerLabel}>
                End of the first batch of jobs
              </span>
              <hr className={styles.batchDividerRule} />
            </div>
            <BrowseMoreJobs placement="mobile" country={country} />
          </>
        );
    }

    return (
      <Fragment key={key}>
        {card}
        {injectAd && (
          <JobsBoardBanner
            partners={partners}
            loadedPartners={loadedPartners}
            loadingPartners={loadingPartners}
          />
        )}
        {twineInserts}
      </Fragment>
    );
  };

  const twineCard = (brief) =>
    withInserts(
      <JobCard
        brief={brief}
        openInNewTab
        showJobState
        onClickRole={onClickRole}
        onClickLocation={onClickLocation}
      />,
      `twine-${brief.id}`,
      true
    );

  const externalCard = (job) =>
    withInserts(
      <JobCard {...mapBoardExternalJobToJobCardProps(job)} showJobState />,
      `external-${job.id}`,
      false
    );

  const totalTwine =
    timeline.featured.length +
    timeline.days.reduce((sum, day) => sum + day.twine.length, 0);

  // The Ari card closes today's section, or the first day when today is empty.
  const bestMatchDate = timeline.days.length
    ? (timeline.days.find((day) => day.date === today) || timeline.days[0]).date
    : null;

  const bestMatchCard = showBestMatchCard ? (
    <JobsBestMatchCard
      pageType={pageType}
      auth={auth}
      filterCount={filterCount}
      onBestMatchNavigate={onBestMatchNavigate}
    />
  ) : null;

  // Guests with fewer than a full batch still get Browse more jobs at the end.
  const showTrailingBrowseMore =
    auth === 'guest' && totalTwine > 0 && totalTwine <= JOBS_BROWSE_MORE_INDEX;

  return (
    <div className={styles.timeline} data-testid="jobs-timeline">
      {timeline.featured.length > 0 && (
        <section
          className={styles.day}
          aria-labelledby="jobs-day-featured"
          data-testid="jobs-featured"
        >
          <div className={styles.dayHeading}>
            <h2 id="jobs-day-featured" className={styles.dayTitle}>
              Featured on Twine
            </h2>
          </div>
          {timeline.featured.map(twineCard)}
          {!bestMatchDate && bestMatchCard}
        </section>
      )}

      {timeline.days.map((day) => {
        const heading = formatDayHeading(day.date, today);
        const headingId = `jobs-day-${day.date}`;
        const external = day.external;
        const expanded = Boolean(expandedDays[day.date]);
        const visibleExternal = external
          ? expanded
            ? external.jobs
            : external.jobs.slice(0, EXTERNAL_JOBS_PER_DAY)
          : [];
        const hiddenCount = external
          ? external.total - visibleExternal.length
          : 0;

        const handleShowAll = () => {
          if (!expanded) {
            trackEvent('jobs-external-show-all-clicked', {
              day: heading.relative || 'older',
              total: external.total,
            });
          }
          onShowAllExternal(day.date, external.jobs.length);
        };

        return (
          <section
            key={day.date}
            className={styles.day}
            aria-labelledby={headingId}
            data-testid="jobs-day"
            data-date={day.date}
          >
            <div className={styles.dayHeading}>
              <h2 id={headingId} className={styles.dayTitle}>
                {heading.title}
              </h2>
              {heading.subtitle ? (
                <span className={styles.daySubtitle}>{heading.subtitle}</span>
              ) : null}
            </div>

            {day.twine.length > 0 && (
              <>
                <SectionHeading kind="twine" />
                {day.twine.map(twineCard)}
              </>
            )}

            {visibleExternal.length > 0 && (
              <>
                <SectionHeading kind="external" />
                {visibleExternal.map(externalCard)}
                {hiddenCount > 0 && (
                  <button
                    type="button"
                    className={styles.showAll}
                    onClick={handleShowAll}
                    disabled={Boolean(external.loading)}
                    aria-expanded={expanded}
                    data-testid="jobs-external-show-all"
                  >
                    {expanded
                      ? `Show ${Math.min(
                          hiddenCount,
                          EXTERNAL_JOBS_PER_PAGE
                        )} more jobs from other sites`
                      : showAllExternalLabel(external.total, heading.relative)}
                    <ChevronDown size={14} aria-hidden="true" />
                  </button>
                )}
              </>
            )}

            {day.date === bestMatchDate && bestMatchCard}
          </section>
        );
      })}

      {showTrailingBrowseMore && (
        <BrowseMoreJobs placement="mobile" country={country} />
      )}
    </div>
  );
};

JobsTimeline.propTypes = {
  timeline: PropTypes.shape({
    featured: PropTypes.array.isRequired,
    days: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string.isRequired,
        twine: PropTypes.array.isRequired,
        external: PropTypes.shape({
          total: PropTypes.number,
          jobs: PropTypes.array,
          loading: PropTypes.bool,
        }),
      })
    ).isRequired,
  }).isRequired,
  today: PropTypes.string.isRequired,
  expandedDays: PropTypes.object,
  onShowAllExternal: PropTypes.func,
  onClickRole: PropTypes.func,
  onClickLocation: PropTypes.func,
  partners: PropTypes.array,
  loadedPartners: PropTypes.bool,
  loadingPartners: PropTypes.bool,
  pageType: PropTypes.oneOf(['jobs', 'role', 'country']),
  auth: PropTypes.oneOf(['guest', 'logged_in']),
  filterCount: PropTypes.number,
  onBestMatchNavigate: PropTypes.func,
  showBestMatchCard: PropTypes.bool,
  country: PropTypes.string,
};

JobsTimeline.defaultProps = {
  expandedDays: {},
  onShowAllExternal: () => {},
  pageType: 'jobs',
  auth: 'guest',
  filterCount: 0,
  showBestMatchCard: true,
  country: '',
};

export default JobsTimeline;
